import * as _ from "lodash";
import { IBusinessObject } from "../PA.Core/PA.Core.IF.BO";
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_CubeSourceDataSourceDimensionRow,
    WB_CubeSourceDataSourceRow,
    WB_CubeSourceDimensionRow,
    WB_CubeSourceRow,
    WB_DataObjectRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbCubeSource} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_CubeSource,
    esWB_CubeSource_DataSource,
    esWB_CubeSource_DataSource_Dimension,
    esWB_CubeSource_Dimension, esWB_DataObject, esWB_Dimension
} from "../PA.Core/PAdbSchema";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver"
import {ASync} from "../PA.Core/Basic/ASync"
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import { CubeSourceBOValidation } from "./CubeSourceBO.validation";




export class CubeSourceBO
    implements
        IBusinessObject
{

    //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_CubeSource: WB_CubeSourceRow;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Cube Source'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_CubeSource = null;
    }

    get WB_CubeSource(): WB_CubeSourceRow {
        return this.drWB_CubeSource;
    }
    
    //</editor-fold>

    //<editor-fold desc="IBusinessObject" operations>

    async LoadNew(options?: any): Promise<IBusinessObject> {
        const self = this;

        self.LoadProgressProvider.start("Creating Cube Source");

        let returnValue = self._OnLoadComplete = (async ()=> {
            self.LoadOptions = options || {};
            if("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if(_.isEmpty(this.dbContext)){
                let sError = "Unable to determine dbContext for new Cube Source";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                return;
            }

            //Create record using dbContext
            let newCubeSourceRow: WB_CubeSourceRow = <any> await self.dbContext.createRecordWithKey(esWB_CubeSource)

            newCubeSourceRow.setDefaults();
            self.drWB_CubeSource = newCubeSourceRow;
            //add this to all the BOs
            self.dbContext.add(esWB_CubeSource, newCubeSourceRow);
            self.Identity.setAll(self.WB_CubeSource.cif.WB_CUBESOURCE_ID, self.WB_CubeSource.cif.CUBESOURCE_ID, self.WB_CubeSource.cif.CUBESOURCE_NAME);
            self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
            self.Identity.listenToRecordUpdates(self.WB_CubeSource, "WB_CUBESOURCE_ID", "CUBESOURCE_ID", "CUBESOURCE_NAME");

            self.LoadProgressProvider.finish();
            self._bIsLoaded = true;
            self._OnLoadComplete = null;
            return self;

        })();
        return returnValue
    }

    async Load(anId: number | number, options?: any): Promise<IBusinessObject> {
        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        let returnValue = self._OnLoadComplete = (async ()=> {

            try {
                if (self.bIsLoaded) {
                    let sError = "Cube Source already loaded";
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }

                self.LoadOptions = options || {};
                if ("dbContext" in self.LoadOptions)
                    self.dbContext = self.LoadOptions.dbContext;
                else if (self.dbContext == null)
                    self.dbContext = new PAdbContext();

                if (_.isEmpty(self.dbContext)) {
                    let sError = "Unable to determine dbContext for new Cube Source";
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }

                //Added for record resolver
                if ("RecordResolver" in self.LoadOptions) {
                    recordResolver = (<any>self.LoadOptions).RecordResolver;
                    recordResolverLoadPromise = ASync.resolve();
                } else {
                    recordResolver = CubeSourceBO.createRecordResolver({dbContext: self.dbContext});
                    recordResolverLoadPromise = CubeSourceBO.setupRecordResolver(recordResolver, [anId]);
                }

                await recordResolverLoadPromise

                let aCubeSourceRow: any = recordResolver.get(esWB_CubeSource, anId);

                if (_.isEmpty(aCubeSourceRow)) {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception("Id not found.");
                    return self;
                }

                self.drWB_CubeSource = aCubeSourceRow;
                self.Identity.setAll(self.WB_CubeSource.cif.WB_CUBESOURCE_ID, self.WB_CubeSource.cif.CUBESOURCE_ID, self.WB_CubeSource.cif.CUBESOURCE_NAME);
                self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                self.Identity.listenToRecordUpdates(self.WB_CubeSource, "WB_CUBESOURCE_ID", "CUBESOURCE_ID", "CUBESOURCE_NAME");

                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                self.LoadProgressProvider.finish();
                return self;
            } catch(err){
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    return self
                }
        })();

        return returnValue;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;

        self._bIsValidated = false
        self.ValidateOptions = options || {};

        self.ValidateMessageProvider.clearMessages();
        self.ValidateProgressProvider.start("Validating " + self.WB_CubeSource.cif.CubeSOURCE_TYPE + "(" + self.WB_CubeSource.cif.CubeSOURCE_ID + ")");

        return (async () => {
            //Does no validations yet

            try {
                if (_.isEmpty(self.dbContext)) {
                    let sError = "Unable to determine dbContext to Validate Cube Source";
                    self.ValidateProgressProvider.exception(sError);
                    return self
                }

                await CubeSourceBOValidation.ValidateFields(self)

                if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                    self._bIsValidated = false;
                } else {
                    self._bIsValidated = true;
                }
                self.ValidateProgressProvider.finish();
                return self;
            } catch (err) {
                self._bIsValidated = false;
                self.ValidateProgressProvider.exception(err);
                return self;
            }
        })();
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;

        self.SaveOptions = options || {};
        self._bIsSaved = false

        const wbCubeSource = <IwbCubeSource>self.drWB_CubeSource;
        self.SaveProgressProvider.start("Saving " + wbCubeSource.CUBESOURCE_NAME + "(" + wbCubeSource.CUBESOURCE_ID + ")");

        return (async () => {
            //Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            try {
                if (_.isEmpty(self.dbContext)) {
                    self._bIsSaved = false;
                    let sError = "Unable to determine dbContext to Save Cube Source.";
                    self.SaveProgressProvider.exception(sError);
                    return self
                }

                let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;
                if (bBatch) {
                    saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
                } else {
                    saveFunction = self.dbContext.Save.bind(self.dbContext);
                }


                await self.Validate({})

                if (!self.bIsValidated) {
                    self.SaveProgressProvider.finish();
                    return self;
                }

                let tasks: Promise<any> [] = [];
                tasks.push(saveFunction(esWB_CubeSource, self.drWB_CubeSource, {}))
                tasks.push(saveFunction(esWB_CubeSource_Dimension, self.WB_CubeSource.WB_CubeSourceDimensionRows, {}))
                tasks.push(saveFunction(esWB_CubeSource_DataSource, self.WB_CubeSource.WB_CubeSourceDataSourceRows, {}))
                tasks.push(saveFunction(esWB_CubeSource_DataSource_Dimension,
                    _.flatMap(self.WB_CubeSource.WB_CubeSourceDataSourceRows, (a)=> a.WB_CubeSourceDataSourceDimensionRows), {}))
                await Promise.all(tasks);

                if (bBatch) {
                    self.dbContext.addBatchCompleteListener(() => {
                        self._bIsSaved = true;
                    })
                } else {
                    self._bIsSaved = true;
                }
                self.SaveProgressProvider.finish();
                return self;
            } catch (err) {
                self.SaveProgressProvider.exception(err);
                return self
            }
        })();
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private async DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;

        const wbCubeSource = <IwbCubeSource>self.drWB_CubeSource;
        try {
            self.DeleteMessageProvider.clearMessages();
            self.DeleteProgressProvider.start("Deleting " + wbCubeSource.CubeSOURCE_TYPE + "(" + wbCubeSource.CubeSOURCE_ID + ")");

            // 1. Validate if we can delete the Cube Source

            // 2. If Valid, mark all the records for deletion.
            self.drWB_CubeSource.markDeleted()
            _.forEach(self.drWB_CubeSource.WB_CubeSourceDimensionRows, (a) => a.markDeleted())
            _.forEach(self.drWB_CubeSource.WB_CubeSourceDataSourceRows, (a) => {
                _.forEach(a.WB_CubeSourceDataSourceDimensionRows, (b) => b.markDeleted())
                a.markDeleted()
            })

            if (bSaveNow) {
                await self.SaveUsing(false)
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                return self
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                return self
            }
        } catch (err) {
            self.DeleteProgressProvider.exception(err);
        }
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const cubesource_options: CreateOptions = {
            standardConditions: {
                [esWB_CubeSource]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_CubeSource_Dimension]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_CubeSource_DataSource]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_CubeSource_DataSource_Dimension]: {"DELETED_IND": {"notIn": ['1']}},
            },
            dctIncludeBelongsToNamedRelations: {

            }
        };

        const aRecResolver = new ESRecordResolver(dbContext, cubesource_options);

        return aRecResolver;
    }

    static async setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        await recordResolver.findAllWithRelationships(esWB_CubeSource, ids)
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>

    //<editor-fold desc="add remove operations" operations>

    async addDimension(dimType: string): Promise<WB_CubeSourceDimensionRow> {
        const self = this

        if(_.isEmpty(dimType))
            return

        _.forEach(self.WB_CubeSource.WB_CubeSourceDimensionRows, (aRow) => {
            if(!aRow.isActive)
                return

            if(aRow.cif.DIMENSION_TYPE == dimType)
                return aRow // record already exists
        })

        let aNewRow: WB_CubeSourceDimensionRow = <any> await self.dbContext.createRecordWithKey(esWB_CubeSource_Dimension, {})
        if(_.isEmpty(aNewRow))
            return

        aNewRow.setDefaults()
        aNewRow.WB_CubeSourceRow = self.drWB_CubeSource
        aNewRow.cif.WB_CUBESOURCE_ID = self.drWB_CubeSource.cif.WB_CUBESOURCE_ID
        aNewRow.cif.DIMENSION_TYPE = dimType

        self.dbContext.add(esWB_CubeSource_Dimension, aNewRow)

        await self.syncDataSourceDimensionRows()
        return aNewRow

    }

    async removeDimension(dimType: string): Promise<void> {
        const self = this

        _.forEach(self.WB_CubeSource.WB_CubeSourceDimensionRows, (aRow) => {
            if(!aRow.isActive)
                return

            if(aRow.cif.DIMENSION_TYPE == dimType)
                aRow.markDeleted()
        })

        await self.syncDataSourceDimensionRows()
    }

    async addDataSource(wbDataObjectId: number): Promise<WB_CubeSourceDataSourceRow> {
        const self = this

        let aDataObjectRow: WB_DataObjectRow = await self.dbContext.find(esWB_DataObject, wbDataObjectId)
        if(_.isEmpty(aDataObjectRow))
            return

        let aNewRow: WB_CubeSourceDataSourceRow = <any> await self.dbContext.createRecordWithKey(esWB_CubeSource_DataSource, {})
        if(_.isEmpty(aNewRow))
            return

        aNewRow.setDefaults()
        aNewRow.WB_CubeSourceRow = self.WB_CubeSource
        aNewRow.cif.WB_CUBESOURCE_ID = self.WB_CubeSource.cif.WB_CUBESOURCE_ID
        aNewRow.WB_DataObjectRow = aDataObjectRow
        aNewRow.cif.WB_DATAOBJECT_ID = aDataObjectRow.cif.WB_DATAOBJECT_ID
        aNewRow.cif.FILTER_CONDITION = "{ }"
        aNewRow.cif.ALIAS_ID = aDataObjectRow.cif.DATAOBJECT_ID.replace("/AZR/","")
        self.dbContext.add(esWB_CubeSource_DataSource, aNewRow)

        await self.syncDataSourceDimensionRows()
        return aNewRow
    }

    async removeDataSource(wbCubeSourceDSRId: number): Promise<void> {
        const self = this

        _.forEach(self.WB_CubeSource.WB_CubeSourceDataSourceRows, (aRow) => {
            if(!aRow.isActive)return
            if(aRow.cif.WB_CUBESOURCEDSR_ID == wbCubeSourceDSRId) {
                _.forEach(aRow.WB_CubeSourceDataSourceDimensionRows, (b) => b.markDeleted())
                aRow.markDeleted()
            }
        })
    }

    async syncDataSourceDimensionRows(): Promise<void> {
        const self = this


        // Get the distinct list of dimensions
        let dctDims: {[dimType: string]: string} = {}
        _.forEach(self.WB_CubeSource.WB_CubeSourceDimensionRows, (aRow) => {
            if(!aRow.isActive)
                return

            if(_.isEmpty(aRow.cif.DIMENSION_TYPE))
                return

            dctDims[aRow.cif.DIMENSION_TYPE] = aRow.cif.DIMENSION_TYPE
        })

        for(let aRow of self.WB_CubeSource.WB_CubeSourceDataSourceRows){
            if(!aRow.isActive)
                continue

            let dctMissing = _.clone(dctDims)
            _.forEach(aRow.WB_CubeSourceDataSourceDimensionRows, (dimRow) => {
                if(!dimRow.isActive)
                    return

                if(!(dimRow.cif.DIMENSION_TYPE in dctDims)){
                    dimRow.markDeleted()
                    return
                }
                delete dctMissing[dimRow.cif.DIMENSION_TYPE]
            })

            for(let dimMissing of _.values(dctMissing)){
                let aNewRow: WB_CubeSourceDataSourceDimensionRow = <any> await self.dbContext.createRecordWithKey(esWB_CubeSource_DataSource_Dimension, {})
                aNewRow.setDefaults()
                aNewRow.cif.DIMENSION_TYPE = dimMissing
                aNewRow.WB_CubeSourceDataSourceRow = aRow
                aNewRow.cif.WB_CUBESOURCEDSR_ID = aRow.cif.WB_CUBESOURCEDSR_ID
                self.dbContext.add(esWB_CubeSource_DataSource_Dimension, aNewRow)
            }
        }


    }

    //</editor-fold>

}
PAEnvironment.registerBusinessObjectClass( CubeSourceBO , "CubeSourceBO", "CubeSource")